"use client";

import React from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

const DOTS = "…" as const;

/**
 * Builds a compact page range with ellipses:
 *   1 … 4 5 6 … 20
 * Returns every page when the total is small enough to display fully.
 */
function getPaginationRange(
  currentPage: number,
  totalPages: number,
  siblingCount = 1,
): (number | typeof DOTS)[] {
  // first + last + current + 2*siblings + 2 ellipses
  const maxSlots = siblingCount * 2 + 5;

  if (totalPages <= maxSlots) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);

  const showLeftDots = leftSibling > 2;
  const showRightDots = rightSibling < totalPages - 1;

  const range: (number | typeof DOTS)[] = [1];
  if (showLeftDots) range.push(DOTS);

  for (
    let page = Math.max(leftSibling, 2);
    page <= Math.min(rightSibling, totalPages - 1);
    page++
  ) {
    range.push(page);
  }

  if (showRightDots) range.push(DOTS);
  range.push(totalPages);

  return range;
}

const Chevron: React.FC<{ direction: "left" | "right" }> = ({ direction }) => (
  <svg
    width="8"
    height="12"
    viewBox="0 0 6 14"
    fill="none"
    aria-hidden="true"
    className={direction === "right" ? "rotate-180" : ""}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 1 1 7l6 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const navButton =
  "flex h-9 w-10.5 items-center justify-center rounded-md border border-[#E5E5E5] bg-white text-[#6B7280] transition-colors duration-300 hover:border-[#FF8A3D] hover:text-[#FF8A3D] disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer";

/**
 * Gradient-border wrapper: a 1px gradient frame around a white inner button.
 * (Tailwind can't do gradient borders directly, so we use a padded
 * gradient background with a white button inside.)
 */
const GradientFrame: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <span className="inline-flex rounded-lg bg-gradient-to-b from-[#FFBC41] to-[#FF5758] p-[1px]">
    {children}
  </span>
);

/**
 * Controlled, accessible pagination with prev/next controls and ellipses.
 * Reusable for any paginated list — not coupled to testimonials.
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className = "",
}) => {
  if (totalPages <= 1) return null;

  const pages = getPaginationRange(currentPage, totalPages);

  return (
    <nav
      aria-label="Pagination"
      className={`flex items-center justify-center gap-6 ${className}`}
    >
      <button
        type="button"
        className={navButton}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Page précédente"
      >
        <Chevron direction="left" />
      </button>

      <ul className="flex items-center gap-6">
        {pages.map((page, index) =>
          page === DOTS ? (
            <li
              key={`dots-${index}`}
              aria-hidden="true"
              className="select-none px-1 text-[#9CA3AF]"
            >
              {DOTS}
            </li>
          ) : (
            <li key={page}>
              {page === currentPage ? (
                <GradientFrame>
                  <button
                    type="button"
                    onClick={() => onPageChange(page)}
                    aria-current="page"
                    aria-label={`Page ${page}`}
                    className="flex h-9 w-10.5 cursor-pointer items-center justify-center rounded-lg bg-[#FC7F16] text-[14px] font-bold text-white shadow-sm transition-colors duration-300"
                  >
                    {page}
                  </button>
                </GradientFrame>
              ) : (
                <GradientFrame>
                  <button
                    type="button"
                    onClick={() => onPageChange(page)}
                    aria-label={`Page ${page}`}
                    className="flex h-9 w-10.5 cursor-pointer items-center justify-center rounded-lg bg-white text-[14px] font-bold text-[#FF7A2F] transition-colors duration-300 hover:bg-[#FFF4EC]"
                  >
                    {page}
                  </button>
                </GradientFrame>
              )}
            </li>
          ),
        )}
      </ul>

      <button
        type="button"
        className={navButton}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Page suivante"
      >
        <Chevron direction="right" />
      </button>
    </nav>
  );
};

export default Pagination;
